import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus } from 'lucide-react';
import { stcResourceService, StcResourceDto } from '../../../../../services/stcResourceService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { ResourceBulkUpdateModal } from './ResourceBulkUpdateModal';
import { ResourceCreateModal } from './ResourceCreateModal';

interface ResourceDataTableProps {
    refreshTrigger?: number;
}

export const ResourceDataTable: React.FC<ResourceDataTableProps> = ({
    refreshTrigger
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const companyNo = user?.companyNo || '';

    // Core data state
    const [data, setData] = useState<StcResourceDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedResourceIds, setSelectedResourceIds] = useState<number[]>([]);

    // Modal state
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Inline editing
    const inlineEditConfig = useInlineEdit<StcResourceDto>({
        getRowId: (row) => row.id,
        onSave: async (_originalRow, editedData) => {
            await stcResourceService.update(
                companyNo,
                editedData.resourceNo as number,
                { description1: (editedData.description1 as string) || '' }
            );
        },
        onSaveSuccess: () => loadData(),
        onSaveError: (err) => setError(err.message || t('manager.modules.adminResources.resourceTable.errorSave', {}, 'Feil ved lagring'))
    });

    // Load data on mount and refresh
    useEffect(() => {
        loadData();
    }, [companyNo]);

    useEffect(() => {
        if (refreshTrigger && refreshTrigger > 0) {
            loadData();
        }
    }, [refreshTrigger]);

    const loadData = useCallback(async () => {
        if (!companyNo) return;

        setIsLoading(true);
        setError(null);

        try {
            let resources: StcResourceDto[] = [];
            try {
                resources = await stcResourceService.getCurrent();
            } catch {
                try {
                    resources = await stcResourceService.getAll();
                } catch {
                    resources = [];
                }
            }
            setData(resources);
        } catch (err: any) {
            console.error('Error loading resources:', err);
            setError(t('manager.modules.adminResources.resourceTable.errorLoad', {}, 'Kunne ikke laste ressurser. Prøv igjen.'));
        } finally {
            setIsLoading(false);
        }
    }, [companyNo, t]);

    // Column factory
    const columnFactory = createColumnFactory<StcResourceDto>();

    // Format date for display
    const formatDate = (value: any): string => {
        if (!value) return '-';
        try {
            const date = new Date(value);
            return date.toLocaleDateString('nb-NO', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return String(value);
        }
    };

    // Column definitions - no inline editing, just display
    const columns: ColumnDefinition<StcResourceDto>[] = useMemo(() => [
        columnFactory.createNumberColumn('resourceNo', 'resourceNo', t('manager.modules.adminResources.resourceTable.colResourceNo', {}, 'Ressurs nr.'), {
            minWidth: 110,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
            minWidth: 250,
            sortable: true,
            filterType: 'both',
            editable: true
        }),
        columnFactory.createTextColumn('createdBy', 'createdBy', t('manager.modules.adminResources.resourceTable.colCreatedBy', {}, 'Opprettet av'), {
            minWidth: 130,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createDateColumn('created', 'created', t('manager.modules.adminResources.resourceTable.colCreated', {}, 'Opprettet'), {
            minWidth: 160,
            sortable: true,
            filterType: 'none',
            formatter: formatDate
        }),
        columnFactory.createTextColumn('updatedBy', 'updatedBy', t('manager.modules.adminResources.resourceTable.colUpdatedBy', {}, 'Endret av'), {
            minWidth: 130,
            sortable: true,
            filterType: 'both'
        }),
        columnFactory.createDateColumn('lastUpdate', 'lastUpdate', t('manager.modules.adminResources.resourceTable.colLastUpdate', {}, 'Sist endret'), {
            minWidth: 160,
            sortable: true,
            filterType: 'none',
            formatter: formatDate
        })
    ], [t]);

    // Bulk edit handler
    const handleBulkEdit = useCallback(() => {
        if (selectedResourceIds.length === 0) {
            return;
        }
        setShowBulkEditModal(true);
    }, [selectedResourceIds.length]);

    // Handle modal success
    const handleUpdateSuccess = async () => {
        await loadData();
        setSelectedResourceIds([]);
        setShowBulkEditModal(false);
    };

    const handleDeleteSuccess = async () => {
        await loadData();
        setSelectedResourceIds([]);
        setShowBulkEditModal(false);
    };

    const handleCreateSuccess = async () => {
        await loadData();
        setShowCreateModal(false);
    };

    // Table actions
    const tableActions: TableAction<StcResourceDto>[] = useMemo(() => [
        {
            id: 'create',
            label: t('manager.modules.adminResources.resourceTable.newResource', {}, 'Ny ressurs'),
            icon: Plus,
            onClick: () => setShowCreateModal(true),
            variant: 'primary'
        },
        {
            id: 'bulk-edit',
            label: t('manager.modules.adminResources.resourceTable.maintenanceAction', { '0': selectedResourceIds.length }, `Vedlikehold (${selectedResourceIds.length})`),
            icon: Edit,
            onClick: () => handleBulkEdit(),
            disabled: () => selectedResourceIds.length === 0,
            variant: 'primary'
        }
    ], [selectedResourceIds.length, handleBulkEdit, t]);

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-50">
            <div className="flex-1 min-h-0">
                <WarehouseTable
                    data={data}
                    columns={columns}
                    config={{
                        showPagination: true,
                        showColumnConfig: true,
                        showGlobalSearch: true,
                        showFilters: true,
                        showExport: true,
                        showPrint: true,
                        showSummary: false,
                        defaultPageSize: 50,
                        allowColumnReorder: true,
                        allowColumnResize: true,
                        stickyHeader: true,
                        compactMode: true,
                        persistenceKey: 'resources-table',
                        allowRowSelection: {
                            mode: 'multi',
                            selectAllMode: 'all-data'
                        }
                    }}
                    selectedRows={selectedResourceIds}
                    onSelectionChange={(selectedIds) => {
                        setSelectedResourceIds(selectedIds as number[]);
                    }}
                    getRowId={(item) => item.id}
                    tableActions={tableActions}
                    inlineEditConfig={inlineEditConfig}
                    loading={isLoading}
                    error={error || undefined}
                />
            </div>

            {/* Bulk Update Modal */}
            {showBulkEditModal && selectedResourceIds.length > 0 && (
                <ResourceBulkUpdateModal
                    isOpen={showBulkEditModal}
                    onClose={() => setShowBulkEditModal(false)}
                    selectedResourceIds={selectedResourceIds}
                    data={data}
                    onUpdateSuccess={handleUpdateSuccess}
                    onDeleteSuccess={handleDeleteSuccess}
                />
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <ResourceCreateModal
                    isOpen={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={handleCreateSuccess}
                />
            )}
        </div>
    );
};
